import { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface CancelRescheduleDialogProps {
  appointmentId: string;
  appointmentTitle: string;
  onClose: () => void;
  onSuccess: () => void;
  onCancel: (reason: string) => Promise<void>;
  onReschedule: (data: { reason: string; newStartUtc: string; newEndUtc: string }) => Promise<void>;
}

export function CancelRescheduleDialog({
  appointmentId,
  appointmentTitle,
  onClose,
  onSuccess,
  onCancel,
  onReschedule,
}: CancelRescheduleDialogProps) {
  const [step, setStep] = useState<'confirm' | 'reason' | 'reschedule'>('confirm');
  const [reason, setReason] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newStartHour, setNewStartHour] = useState('09');
  const [newStartMinute, setNewStartMinute] = useState('00');
  const [newEndDate, setNewEndDate] = useState('');
  const [newEndHour, setNewEndHour] = useState('10');
  const [newEndMinute, setNewEndMinute] = useState('00');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirmCancel = () => {
    setStep('reason');
  };

  const handleWantReschedule = () => {
    setStep('reschedule');
  };

  const handleCancelOnly = async () => {
    if (!reason.trim()) {
      toast.error('Por favor, informe o motivo do cancelamento');
      return;
    }

    setIsSubmitting(true);
    try {
      await onCancel(reason);
      toast.success('Consulta cancelada com sucesso');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erro ao cancelar consulta');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReschedule = async () => {
    if (!reason.trim()) {
      toast.error('Por favor, informe o motivo do reagendamento');
      return;
    }

    if (!newStartDate || !newEndDate) {
      toast.error('Por favor, informe a nova data');
      return;
    }

    const newStartUtc = new Date(`${newStartDate}T${newStartHour}:${newStartMinute}:00`).toISOString();
    const newEndUtc = new Date(`${newEndDate}T${newEndHour}:${newEndMinute}:00`).toISOString();

    setIsSubmitting(true);
    try {
      await onReschedule({ reason, newStartUtc, newEndUtc });
      toast.success('Consulta reagendada com sucesso');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Erro ao reagendar consulta');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Cancelar Consulta
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Consulta:</p>
            <p className="font-medium text-gray-900">{appointmentTitle}</p>
          </div>

          {step === 'confirm' && (
            <div className="space-y-4">
              <p className="text-gray-700">
                Deseja cancelar esta consulta?
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Voltar
                </button>
                <button
                  onClick={handleWantReschedule}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Reagendar
                </button>
                <button
                  onClick={handleConfirmCancel}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Cancelar Consulta
                </button>
              </div>
            </div>
          )}

          {step === 'reason' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo do Cancelamento *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[100px]"
                  placeholder="Descreva o motivo do cancelamento..."
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setStep('confirm')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Voltar
                </button>
                <button
                  onClick={handleCancelOnly}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Cancelando...' : 'Confirmar Cancelamento'}
                </button>
              </div>
            </div>
          )}

          {step === 'reschedule' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo do Reagendamento *
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                  placeholder="Descreva o motivo do reagendamento..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nova Data de Início *
                  </label>
                  <input
                    type="date"
                    value={newStartDate}
                    onChange={(e) => setNewStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Início *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={newStartHour}
                      onChange={(e) => setNewStartHour(e.target.value.padStart(2, '0'))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="HH"
                    />
                    <span className="self-center">:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={newStartMinute}
                      onChange={(e) => setNewStartMinute(e.target.value.padStart(2, '0'))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="MM"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nova Data de Término *
                  </label>
                  <input
                    type="date"
                    value={newEndDate}
                    onChange={(e) => setNewEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horário de Término *
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={newEndHour}
                      onChange={(e) => setNewEndHour(e.target.value.padStart(2, '0'))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="HH"
                    />
                    <span className="self-center">:</span>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={newEndMinute}
                      onChange={(e) => setNewEndMinute(e.target.value.padStart(2, '0'))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="MM"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setStep('confirm')}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Voltar
                </button>
                <button
                  onClick={handleReschedule}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Reagendando...' : 'Confirmar Reagendamento'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
